import {
  Component,
  OnInit,
  inject,
  DestroyRef,
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
  ChangeDetectorRef
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { IonicModule, ModalController } from '@ionic/angular';
import { interval, from, exhaustMap, filter } from 'rxjs';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { WindowService } from 'src/shared/providers/window.service';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { Util } from 'src/shared/util/util';
import { Genome } from 'src/providers/genome/genome';

import { OrderRestockPage } from 'src/pages/inventory/order-restock/order-restock.page';
import { ConfirmPage, ConfirmPageParamsModel } from 'src/pages/confirm/confirm.component';

import { formatMoney } from 'src/shared/services/restock-order-state.pipe';
import {
  isWalmartProductLink,
  productTitleFromLink,
  packLabel,
  unitsPerPackFor,
} from 'src/models/restocking-brand.model';

import {
  RestockingOrdersService,
  RestockOrder,
  RestockOrderState,
  RestockOrderError
} from 'src/shared/services/restocking-orders.service';

import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { SharedModule } from 'src/shared/shared.module';
import { RestockAssignJobComponent } from 'src/shared/components/restock-assign-job/restock-assign-job.component';

interface TimelineStepVM {
  label: string;
  detail: string;
  timeText: string;
  status: 'complete' | 'current' | 'upcoming';
  // Pre-built class for the shared .restock-item-stepper markup.
  stepClass: string;
  // Vendor order number, shown under the detail on the "Order Placed" step only.
  orderNumberText?: string;
}

// The four things that actually happen to a restock order, regardless of how
// many internal state transitions back them.
const TIMELINE_STEPS = [
  { label: 'Concierge Review', detail: 'TIDY concierges are reviewing and finalizing your cart.' },
  { label: 'Order Placed', detail: 'Your concierge placed the order with the vendor.' },
  { label: 'Shipment Delivered', detail: 'The shipment arrived at your property.' },
  { label: 'Items Restocked', detail: 'A pro restocked these items at your property.' }
];

interface ItemRowVM {
  roomObjectId: number;
  sku: string;
  name: string;
  icon: string;
  // Customer's photo of the item (from its room-object record); shown as the
  // thumbnail when present, falling back to the category icon.
  photoUrl: string;
  // Model line under the name (only when it exists and differs from the name).
  modelText: string;
  qtyText: string;
  // Stock context for the row ("12 on hand · target 99"), empty when the
  // item has no configured levels.
  stockText: string;
  // Why the item is in the cart — 'Low Stock' / 'Opportunistic Buy' / ''.
  levelLabel: string;
  levelClass: string;
  lineTotalText: string;
  oldLineTotalText: string;
  // Rich resolved-product preview (Walmart thumbnail + title + price/pack + link).
  // productLink is empty when the resolved product isn't a Walmart Business URL —
  // the row falls back to the plain resolvedText line then.
  productLink: string;
  productTitle: string;
  productPriceLabel: string;
  resolvedText: string;
  substitutionNote?: string;
  hasBackups: boolean;
  backupsShowText: string;
  showBackups: boolean;
  backups: { text: string }[];
}

function ordinal(rank: number): string {
  switch (rank) {
    case 1: return '1st';
    case 2: return '2nd';
    case 3: return '3rd';
    default: return `${rank}th`;
  }
}

// Index of "Order Placed" in TIMELINE_STEPS — the step the vendor order number
// belongs to.
const ORDER_PLACED_STEP = 1;

// States where the concierge has placed the order, so real vendor prices exist.
// Before this, nothing on the page can quote a price.
const PRICED_STATES: RestockOrderState[] = ['ordered', 'delivered', 'restocked'];

// The backend stores vendor names as typed by the concierge ("walmart"), but they
// are proper nouns in the UI.
function titleCase(value: string): string {
  return (value || '')
    .split(' ')
    .map((word) => (word ? word[0].toUpperCase() + word.slice(1) : word))
    .join(' ');
}

// Refresh cadence for the concierge-driven order state while the customer is on
// the detail page. 15s is responsive enough for a fulfillment flow without
// hammering the endpoint.
const POLL_INTERVAL_MS = 15_000;

function fmtDateTime(value: string): string {
  return new Date(value).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

@Component({
  selector: 'restock-order-page',
  templateUrl: 'restock-order.page.html',
  standalone: true,
  imports: [CommonModule, IonicModule, TidyUiComponentsModule, SharedModule, RestockAssignJobComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class RestockOrderPage implements OnInit {
  private readonly navCtrl = inject(CustomNavController);
  private readonly storage = inject(TidyStorage);
  private readonly rightSidePanelService = inject(RightSidePanelService);
  private readonly restockingService = inject(RestockingOrdersService);
  private readonly genome = inject(Genome);
  private readonly route = inject(ActivatedRoute);
  private readonly modalController = inject(ModalController);
  private readonly util = inject(Util);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  readonly windowService = inject(WindowService);

  loaded = false;
  notFound = false;
  readonly skeletonSteps = [0, 1, 2, 3];
  readonly skeletonRows = [0, 1, 2];
  isRightSideContent = false;
  dialogParams: any;

  orderId: number;
  state: RestockOrderState;

  orderTitle = 'Order Details';
  propertySubtitle = '';
  hideBackButton = false;

  // The current order, passed to the Assign-to-Job control (card gCUZuCjf).
  orderForAssign: RestockOrder | undefined;

  timelineSteps: TimelineStepVM[] = [];
  // Detail sentence for whichever step is currently active, rendered under the stepper.
  currentStepDetail = '';

  itemRows: ItemRowVM[] = [];

  finalTotalText = '';
  // Subtotal / tax breakdown, shown once the placed order has been read back
  // (tax present). showBreakdown gates the three-row total in the header.
  subtotalText = '';
  taxText = '';
  showBreakdown = false;
  orderNumberText = '';
  // Delivery date + scraped time-of-day window ("Mon, Aug 24 · 11am–1pm").
  deliveryText = '';

  // Vendor pricing only exists once the concierge has actually placed the order,
  // so every price on this page stays hidden until then.
  showPrices = false;

  orderDropdownMenu: { label: string; action: string }[] = [];

  // Keyed by roomObjectId, not sku: until the concierge resolves a brand the sku
  // is null for every item, so a sku-keyed set would expand all rows at once.
  private expandedIds = new Set<number>();

  // Room-object records for the order's property, joined into the item rows for
  // the real category icon, the customer's item photo, the model name, and live
  // quantities (the order line only carries a snapshot).
  private roomObjectsById = new Map<number, any>();
  private roomObjectsAddressId?: number;

  boundEditCart = this.editCart.bind(this);
  boundCancelOrder = this.cancelOrder.bind(this);

  async ngOnInit() {
    this.isRightSideContent = await this.storage.retrieve('dialog-right-side-open') || false;
    if (this.isRightSideContent) {
      this.dialogParams = await this.storage.retrieve('dialog-params');
    }
    const routeParam = Number(this.route.snapshot.paramMap.get('orderId'));
    this.orderId = this.navCtrl.getParam('orderId') || this.dialogParams?.orderId || routeParam;
    this.hideBackButton = this.navCtrl.getParam('hideBackButton') || this.dialogParams?.hideBackButton || false;
    if (this.isRightSideContent) {
      this.rightSidePanelService.setDialogPageTitle(`Order #${this.orderId}`);
    }
    // Fetch the full order (items + timeline) into the cache before subscribing so
    // the first rebuild renders it; the subscription then keeps it live on mutation.
    try {
      await this.restockingService.getOrderDetail(this.orderId);
    } catch {
      this.util.showError('Unable to load this order. Please try again.');
    }
    this.restockingService.orders$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.rebuild());
    this.rightSidePanelService.dropDownMenuAction$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((action) => this.runMenuAction(action));
    this.startPolling();
    this.loadRoomObjects();
  }

  // One request for the property's room objects, joined into the item rows by
  // room_object_id. Non-blocking and failure-safe: rows render from the order
  // line alone until (unless) this arrives, then rebuild fills in photos/icons.
  private async loadRoomObjects() {
    const order = this.restockingService.getOrder(this.orderId);
    if (!order?.propertyId || this.roomObjectsAddressId === order.propertyId) {
      return;
    }
    try {
      const objects = await this.genome.getRoomsObjects(order.propertyId);
      this.roomObjectsAddressId = order.propertyId;
      this.roomObjectsById = new Map((objects || []).map((object) => [object.id, object]));
      this.rebuild();
    } catch {
      // ignore — the order page works without the room-object join
    }
  }

  // The concierge drives state/timeline/tracking from C.Tidy; poll the detail
  // endpoint so the customer sees those updates without a manual refresh.
  // getOrderDetail refreshes the cache and emits orders$, which rebuilds the view
  // through the subscription above. exhaustMap drops a tick if the previous
  // refresh is still in flight; polling stops once the order is terminal (nothing
  // left to change) and on destroy.
  private startPolling() {
    interval(POLL_INTERVAL_MS)
      .pipe(
        filter(() => !this.isTerminalState()),
        exhaustMap(() => from(this.restockingService.getOrderDetail(this.orderId).catch(() => undefined))),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private isTerminalState(): boolean {
    return this.state === 'restocked' || this.state === 'cancelled';
  }

  private rebuild() {
    const order = this.restockingService.getOrder(this.orderId);
    this.loaded = true;
    if (!order) {
      this.notFound = true;
      this.cdr.markForCheck();
      return;
    }
    this.notFound = false;
    this.orderForAssign = order;
    this.state = order.state;
    this.orderTitle = `Order #${order.id}`;
    this.propertySubtitle = order.propertyName;
    if (this.isRightSideContent) {
      this.rightSidePanelService.setDialogPageSubtitle(order.propertyName);
    }
    this.showPrices = PRICED_STATES.includes(order.state);
    // Set before buildTimeline — the "Order Placed" step renders this.
    this.orderNumberText = order.orderNumber
      ? `${titleCase(order.vendor)} order #${order.orderNumber}`
      : '';
    this.buildTimeline(order);
    this.buildItems(order);
    this.finalTotalText = order.finalTotal !== undefined && order.finalTotal !== null
      ? formatMoney(order.finalTotal)
      : '';
    // Subtotal / tax breakdown once the placed order carries tax; derive the
    // subtotal from total − tax when only tax is sent.
    const tax = order.tax;
    const hasTax = tax !== undefined && tax !== null;
    this.showBreakdown = this.showPrices && !!this.finalTotalText && hasTax;
    this.taxText = hasTax ? formatMoney(tax) : '';
    const subtotal = order.subtotal ?? (order.finalTotal !== undefined && hasTax ? order.finalTotal - tax : undefined);
    this.subtotalText = subtotal !== undefined && subtotal !== null ? formatMoney(subtotal) : '';
    this.deliveryText = this.buildDeliveryText(order);
    this.buildMenu(order.state);
    this.cdr.markForCheck();
  }

  // Delivery date ("Mon, Aug 24") plus the scraped time-of-day window when we
  // have it. The ETA is a bare date (YYYY-MM-DD) — parse it as local so it never
  // slips a day across the UTC boundary.
  private buildDeliveryText(order: RestockOrder): string {
    if (!order.eta) {
      return '';
    }
    const parts = /^(\d{4})-(\d{2})-(\d{2})/.exec(order.eta);
    const date = parts
      ? new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]))
      : new Date(order.eta);
    const label = isNaN(date.getTime())
      ? order.eta
      : date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    return order.deliveryWindow ? `${label} · ${order.deliveryWindow}` : label;
  }

  private buildMenu(state: RestockOrderState) {
    this.orderDropdownMenu = state === 'preview'
      ? [
          { label: 'Edit Cart', action: 'restock-editCart' },
          { label: 'Cancel Order', action: 'restock-cancelOrder' }
        ]
      : [];
    if (this.isRightSideContent) {
      this.rightSidePanelService.setHeaderDropdownMenu(this.orderDropdownMenu);
    }
  }

  private runMenuAction(action: string) {
    if (action === 'restock-editCart') {
      this.editCart();
    } else if (action === 'restock-cancelOrder') {
      this.cancelOrder();
    }
  }

  // Maps the order state onto the step that is currently in progress (-1 when
  // cancelled, so nothing pulses).
  private currentStepIndex(state: RestockOrderState): number {
    switch (state) {
      case 'preview':
      case 'finalizing':
        return 0;
      case 'ordered':
        return 2;
      case 'delivered':
        return 3;
      case 'restocked':
        return TIMELINE_STEPS.length;
      default:
        return -1;
    }
  }

  private buildTimeline(order: RestockOrder) {
    const at = [
      order.createdAt,
      order.timeline.find((event) => event.label === 'Order placed')?.at,
      order.timeline.find((event) => event.label === 'Delivered')?.at,
      order.timeline.find((event) => event.label === 'Items restocked')?.at
    ];
    const currentIndex = this.currentStepIndex(order.state);
    this.timelineSteps = TIMELINE_STEPS.map((step, index) => {
      const status: TimelineStepVM['status'] = currentIndex === -1 || index > currentIndex
        ? 'upcoming'
        : index === currentIndex ? 'current' : 'complete';
      return {
        label: step.label,
        detail: step.detail,
        timeText: at[index] ? fmtDateTime(at[index]) : '',
        status,
        // Drives the shared horizontal stepper (same classes as the item page's
        // Current Order card), so the two views can't drift apart visually.
        stepClass: `restock-item-step ${status === 'complete' ? 'done' : status === 'current' ? 'current' : ''}`.trim(),
        // The vendor order number belongs to "Order Placed" (index 1) — that's the
        // event it identifies, and it only exists once the concierge has ordered.
        orderNumberText: index === ORDER_PLACED_STEP ? this.orderNumberText : ''
      };
    });
    // The stepper only has room for a label, so the "what's happening now"
    // sentence moves underneath it.
    this.currentStepDetail = this.timelineSteps.find((step) => step.status === 'current')?.detail || '';
  }

  private buildItems(order: RestockOrder) {
    this.itemRows = order.items.map((item) => {
      const lineTotal = Math.round(item.resolved.unitPrice * item.orderQuantity * 100) / 100;
      const ranked = [...item.candidates].sort((a, b) => a.rank - b.rank);
      const oldLineTotal = item.previousUnitPrice
        ? Math.round(item.previousUnitPrice * item.orderQuantity * 100) / 100
        : null;
      const roomObject = this.roomObjectsById.get(item.roomObjectId);
      const model = roomObject?.model?.model;
      const resolvedName = this.productDisplayName(item.resolved.brand, item.resolved.productName);
      const productLink = this.resolvedProductLink(item.resolved.productName);
      // The resolved unit price is the vendor price for the chosen product, so
      // surface it as the item's per-pack value as soon as it's resolved.
      const productPriceLabel = item.resolved.unitPrice > 0 ? `${formatMoney(item.resolved.unitPrice)}/pack` : '';
      return {
        roomObjectId: item.roomObjectId,
        sku: item.resolved.sku,
        name: roomObject?.name || item.name,
        icon: roomObject?.model?.category?.default_icon_url || item.icon || 'assets/svg/cube-outline.svg',
        photoUrl: roomObject?.photo_url || '',
        modelText: model && model !== (roomObject?.name || item.name) ? model : '',
        qtyText: `× ${packLabel(item.orderQuantity, unitsPerPackFor(item.resolved, roomObject))}`,
        stockText: this.stockText(item, roomObject),
        levelLabel: this.levelLabel(item.level),
        levelClass: `restock-level-chip ${item.level}`,
        lineTotalText: lineTotal > 0 ? formatMoney(lineTotal) : 'Pending',
        oldLineTotalText: oldLineTotal !== null ? formatMoney(oldLineTotal) : '',
        productLink,
        productTitle: resolvedName,
        productPriceLabel,
        // No-link fallback line: resolved name with its price/pack appended.
        resolvedText: resolvedName
          ? `${resolvedName}${productPriceLabel ? ` — ${productPriceLabel}` : ''}`
          : '',
        substitutionNote: item.resolved.substitutionNote,
        hasBackups: ranked.length > 0,
        backupsShowText: `Show ${ranked.length} backup${ranked.length === 1 ? '' : 's'}`,
        showBackups: this.expandedIds.has(item.roomObjectId),
        backups: ranked.map((candidate) => ({
          text: `${ordinal(candidate.rank)}: ${this.productDisplayName(candidate.brand, candidate.productName)}${this.priceSuffix(candidate.unitPrice)}${
             candidate.inStock ? '' : ' (out of stock)'
             }${candidate.sku && candidate.sku === item.resolved.sku ? ' (selected)' : ''}`
        }))
      };
    });
  }

  // Stock context: what's on hand and the target level. Prefers the
  // live room-object count over the order line's snapshot. Each part is optional
  // (a null means the customer never configured it), so we only join what exists
  // rather than printing "null on hand".
  private stockText(
    item: { currentQuantity: number | null; restockToQuantity: number | null },
    roomObject?: { current_quantity?: number | null; max_quantity?: number | null }
  ): string {
    const parts: string[] = [];
    const current = roomObject?.current_quantity ?? item.currentQuantity;
    const target = item.restockToQuantity ?? roomObject?.max_quantity;
    if (current !== null && current !== undefined) {
      parts.push(`${current} on hand`);
    }
    if (target !== null && target !== undefined) {
      parts.push(`target ${target}`);
    }
    return parts.join(' · ');
  }

  // Why the item is in the cart. 'ok' items (manually added) get no badge.
  private levelLabel(level: 'low' | 'opportunistic' | 'ok'): string {
    switch (level) {
      case 'low':
        return 'Low Stock';
      case 'opportunistic':
        return 'Opportunistic Buy';
      default:
        return '';
    }
  }

  // The resolved product name frequently arrives as a bare Walmart Business URL
  // (brand null). Treat that as the product's link so the row can render a rich
  // preview; empty when the resolved field isn't a Walmart product link.
  private resolvedProductLink(productName?: string): string {
    return productName && isWalmartProductLink(productName) ? productName : '';
  }

  // Display name for a resolved product: brand + name, where the name is derived
  // from the Walmart slug when productName is a URL (so a pasted link never shows
  // through). Empty when nothing is resolved yet.
  private productDisplayName(brand?: string, productName?: string): string {
    const link = this.resolvedProductLink(productName);
    const name = link ? (productTitleFromLink(link) || 'Walmart product') : (productName ?? '');
    return `${brand ?? ''} ${name}`.trim();
  }

  // Only quote a price once the concierge has placed the order — before that the
  // number would be a guess, so the price is simply omitted.
  private priceSuffix(unitPrice: number, unit = ''): string {
    if (!this.showPrices || !(unitPrice > 0)) {
      return '';
    }
    return ` — ${formatMoney(unitPrice)}${unit}`;
  }

  // Open the item's detail modal/page. Resolve ItemDetailsPage from the eager
  // route registry rather than importing it — an import edge from this eagerly
  // evaluated page can reorder module init and break unrelated base classes on
  // boot. navigateTo handles the desktop panel vs mobile push.
  goToItem(row: ItemRowVM, event?: Event) {
    event?.stopPropagation();
    const order = this.restockingService.getOrder(this.orderId);
    const component = this.rightSidePanelService.getComponentFromRoute('item-details');
    const params = {
      itemId: row.roomObjectId,
      addressId: order?.propertyId,
      cameFromRoute: 'restocking'
    };
    this.rightSidePanelService.navigateTo(`item-details/${row.roomObjectId}`, params, component);
  }

  // Open the resolved product's Walmart Business page. Stops propagation so
  // tapping the preview opens the product instead of the item's detail page.
  goToProduct(link: string, event?: Event) {
    event?.stopPropagation();
    if (link) {
      this.util.openUrl(link);
    }
  }

  toggleItemBackups(row: ItemRowVM, event?: Event) {
    event?.stopPropagation();
    if (this.expandedIds.has(row.roomObjectId)) {
      this.expandedIds.delete(row.roomObjectId);
    } else {
      this.expandedIds.add(row.roomObjectId);
    }
    row.showBackups = this.expandedIds.has(row.roomObjectId);
    this.cdr.markForCheck();
  }

  private editCart() {
    this.restockingService.clearCartDraft();
    if (this.windowService.isDesktopRes) {
      this.rightSidePanelService.navigateTo('order-restock', { editOrderId: this.orderId }, OrderRestockPage);
    } else {
      this.navCtrl.navigateForward('order-restock', { editOrderId: this.orderId });
    }
  }

  async cancelOrder() {
    const params: ConfirmPageParamsModel = {
      title: 'Cancel order?',
      body: 'This will cancel your preview cart. Your Concierge will not place the order.',
      backText: 'Keep Order',
      confirmText: 'Cancel Order',
      confirmAction: this.confirmCancel.bind(this)
    };
    const modal = await this.modalController.create({
      component: ConfirmPage,
      componentProps: params,
      animated: false,
      cssClass: 'confirm-modal'
    });
    modal.present();
  }

  private async confirmCancel() {
    try {
      await this.restockingService.cancelOrder(this.orderId);
      this.util.showSuccess('Order cancelled');
    } catch (error) {
      // The backend blocks cancelling once the order is placed — show its reason.
      const message = error instanceof RestockOrderError && error.message
        ? error.message
        : 'Unable to cancel this order. Please try again.';
      this.util.showError(message);
    } finally {
      this.modalController.dismiss();
    }
  }
}
